import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DropdownArrowMore from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid/Grid';
import _ from 'lodash';
import { Checkbox, TextField, FormControlLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete/Autocomplete';
import { withStyles } from '@material-ui/styles';
import { SPACES } from 'common/constants/sizes';
import { Flex } from './layout-components';
import Text from './Text';
import Select from './Select';
import ColorPickerPallete from './ColorPickerPallete';
import styled from 'styled-components';

const TextWrapper = styled.div`
  .MuiOutlinedInput-root {
    border-radius: 4px !important;
  }
`;

export const FORM_CONTROL_ENUM = {
  TEXT: 'text',
  SELECT: 'select',
  CHECKBOX: 'checkbox',
  COLOR_PICKER: 'color-picker',
  MULTI_SELECT: 'multi-select',
  CUSTOM_COMPONENT: 'custom-component'
};

export default class FormBuilder extends Component {
  constructor(props) {
    super(props);

    this.state = {
      fields: props.fields,
      data: props.data || {},
      isTouched: false
    };
  }

  componentDidMount() {
    const { fields = [], onChange } = this.props;
    const { data } = this.state;

    this.setState({
      fields: fields.map(f => ({
        ...f,
        dirty: false
      }))
    });

    if (onChange) {
      onChange(data, this.isValid());
    }
  }

  submit() {
    const { onSubmit } = this.props;
    const { data } = this.state;

    onSubmit(data);
  }

  touch() {
    this.setState({
      isTouched: true
    });
  }

  dataChanged(key, value) {
    const { data, fields } = this.state;

    this.setState(
      {
        data: {
          ...data,
          [key]: value
        },
        fields: [
          ...fields.map(f => ({
            ...f,
            dirty: f.dirty || key === f.key
          }))
        ]
      },
      () => {
        this.props.onChange(this.state.data, this.isValid());
      }
    );
  }

  isValid() {
    const { data, fields } = this.state;

    const isValid = fields.reduce((acc, f) => {
      return acc && (!f.validationFunc || f.validationFunc(data[f.key], data));
    }, true);

    return isValid;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !_.isEqual(_.omit(nextProps, 'data'), _.omit(this.props, 'data')) ||
      !_.isEqual(this.state, nextState)
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      !_.isEqual(_.omit(nextProps, 'data'), _.omit(this.props, 'data')) &&
      nextProps.data
    ) {
      this.setState(
        {
          data: nextProps.data
        },
        () => {
          this.props.onChange(nextProps.data, this.isValid());
        }
      );
    }
  }

  render() {
    const { onSubmit, validateOnTouch } = this.props;
    const { data, isTouched, fields } = this.state;
    const Fields = fields.map((f, idx) => {
      const { withLabel = true, type, key, disabled = false } = f;
      let Field = null;

      switch (type) {
        case FORM_CONTROL_ENUM.TEXT:
          Field = (
            <TextWrapper>
              <TextField
                data-cy={key}
                variant='outlined'
                onSubmit={() => this.submit()}
                onChange={e => {
                  this.dataChanged(key, e.target.value);
                }}
                disabled={disabled}
                value={data[key]}
              />
            </TextWrapper>
          );
          break;
        case FORM_CONTROL_ENUM.SELECT:
          Field = (
            <Select
              onChange={val => {
                this.dataChanged(key, val);
              }}
            >
              {f.options.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          );
          break;
        case FORM_CONTROL_ENUM.CHECKBOX:
          Field = (
            <FormControlLabel
              control={
                <Checkbox
                  data-testid={`checkbox-${key}`}
                  checked={data[key]}
                  onChange={event =>
                    this.dataChanged(key, event.target.checked)
                  }
                  value={key}
                  {...(f.color ? { color: f.color } : {})}
                  {..._.get(f, 'fieldProps', {})}
                />
              }
              label={f.label}
              {...(f.labelProps || {})}
            />
          );
          break;
        case FORM_CONTROL_ENUM.COLOR_PICKER:
          Field = (
            <ColorPickerPallete
              onChange={val => {
                this.dataChanged(key, val);
              }}
              selectedColor={data[key]}
            />
          );
          break;
        case FORM_CONTROL_ENUM.MULTI_SELECT:
          const options = _.get(f, `options`, []);
          const valueExtractor = _.get(f, 'valueExtractor', option => option);
          const selectedOptions = options.filter(option =>
            (data[key] || []).includes(valueExtractor(option))
          );
          const values = selectedOptions.map(valueExtractor);
          const renderOption = _.get(
            f,
            'fieldProps.renderOption',
            (option, values) => (
              <Flex items='center' content='center'>
                <Checkbox checked={values.indexOf(option.id) > -1} />
                {option.name}
              </Flex>
            )
          );
          const { label } = f;

          const StyledAutoComplete = f.fieldStyles
            ? withStyles(f.fieldStyles)(Autocomplete)
            : Autocomplete;

          Field = (
            <StyledAutoComplete
              popupIcon={<DropdownArrowMore />}
              key={idx}
              disableCloseOnSelect
              value={selectedOptions}
              onChange={(e, items) => {
                this.dataChanged(key, items.map(valueExtractor));
              }}
              multiple
              options={options}
              renderTags={() => null}
              // Filter options without case sensitive (regex)
              filterOptions={(searchOptions, { inputValue }) =>
                searchOptions.filter(
                  ({ name: optionName }) =>
                    optionName.search(
                      new RegExp(inputValue.replace(/[^a-zA-Z0-9]/g, ''), 'i')
                    ) !== -1
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant='outlined'
                  placeholder={
                    values.length > 0
                      ? `${values.length} ${label} Selected`
                      : `All ${label}`
                  }
                  margin='normal'
                  fullWidth
                />
              )}
              {..._.get(f, 'fieldProps', {})}
              renderOption={option => renderOption(option, values)}
            />
          );
          break;
        case FORM_CONTROL_ENUM.CUSTOM_COMPONENT:
          Field = f.component;
          break;
        default:
          return null;
      }

      return (
        <Grid item xs={f.gridSize ? f.gridSize : 12} key={`field-${idx}`}>
          <Flex
            direction='column'
            pt='medium'
            {...(f.flexProps ? f.flexProps : {})}
          >
            {withLabel ? (
              <Text size='medium' color='gray' pb={SPACES.tiny} weight='400'>
                {f.label}
              </Text>
            ) : null}
            {Field}
            {f.validationFunc(data[key], data) ||
            (validateOnTouch && !isTouched) ||
            !f.dirty ? null : (
              <Text color='torchRed' size='large' pt={SPACES.tiny} weight='400'>
                {f.errMsg}
              </Text>
            )}
          </Flex>
        </Grid>
      );
    });

    return (
      <form
        onSubmit={() => {
          onSubmit(data);
        }}
      >
        <Grid container spacing={1}>
          {Fields}
        </Grid>
      </form>
    );
  }
}

FormBuilder.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(Object.values(FORM_CONTROL_ENUM)),
      key: PropTypes.string,
      defaultValue: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string
        })
      ),
      onChange: PropTypes.func,
      validationFunc: PropTypes.func,
      errMsg: PropTypes.string
    })
  ),
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  validateOnTouch: PropTypes.bool
};

FormBuilder.defaultProps = {
  fields: [],
  onSubmit: () => {},
  onChange: () => {},
  validateOnTouch: true
};
